/**
 * MonthRaceTrack v4 — "Month Pace"
 *
 * Two concentric Activity Rings (iOS-style):
 *   Outer ring (dim)          = % of month elapsed
 *   Inner ring (status color) = % of budget consumed
 *
 * If inner fills faster than outer → overspending → amber/red.
 * If inner is shorter than outer  → under pace    → green.
 *
 * The gap between the two arcs is the visual heartbeat of the card.
 */
import React, { useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { CheckCircle, AlertTriangle, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useBudget } from '../../contexts/BudgetContext';
import { getDashboardTotalBudget } from '../../utils/budgetCalculations';
import { realTransactions } from '../../utils/transactionFilters';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';

const STATUS = {
  green:   { fg: '#10b981', label: 'On track' },
  amber:   { fg: '#f59e0b', label: 'Tight' },
  red:     { fg: '#ef4444', label: 'Off pace' },
  unknown: { fg: '#64748b', label: 'No budget' },
};

function classifyPace(pctBudget, pctTime, hasBudget) {
  if (!hasBudget) return 'unknown';
  const delta = pctTime - pctBudget;
  if (delta > 5) return 'green';
  if (delta > -5) return 'amber';
  return 'red';
}

/** strokeDasharray props for a circle arc of given pct (0-100). */
function arcProps(r, pct) {
  const c = 2 * Math.PI * r;
  const fill = (Math.min(Math.max(pct, 0), 100) / 100) * c;
  return { strokeDasharray: `${fill.toFixed(2)} ${(c + 2).toFixed(2)}`, strokeDashoffset: 0 };
}

function fmtCompact(n, sym) {
  const v = Math.abs(n || 0);
  if (v >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}M`;
  if (v >= 1000) return `${sym}${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `${sym}${Math.round(v)}`;
}

export default function MonthRaceTrack() {
  const { t } = useTranslation();
  const { themeMode } = useThemeContext();
  const { getCurrencySymbol } = useCurrency();
  const sym = getCurrencySymbol?.() || '€';
  const {
    selectedMonth, selectedYear, isCurrentMonth,
    filterTransactionsByMonth, filterMode, effectiveStartDate, effectiveEndDate,
  } = useDateFilter();
  const { transactions = [], categories = [], loading = false } = useBudget();
  const isDark = themeMode !== 'light';

  const data = useMemo(() => {
    // Exclude recurring TEMPLATES from sums (they double-count instances).
    const realTxs = realTransactions(transactions);
    const periodTx = filterTransactionsByMonth(realTxs);
    const expenses = periodTx
      .filter(tx => tx.type === 'expense' || !tx.type)
      .reduce((s, tx) => s + (Number(tx.amount) || 0), 0);
    const isRange = filterMode === 'range' && effectiveStartDate && effectiveEndDate;
    const budget = getDashboardTotalBudget({
      categories,
      isRangeMode: isRange,
      rangeStart: effectiveStartDate,
      rangeEnd: effectiveEndDate,
      year: selectedYear,
      monthIndex: selectedMonth,
    });
    const today = new Date();
    const dip = isRange
      ? Math.max(1, Math.ceil((effectiveEndDate - effectiveStartDate) / 86400000))
      : new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const de = isRange ? dip : (isCurrentMonth ? Math.min(today.getDate(), dip) : dip);
    const isLive = isCurrentMonth && !isRange;
    const forecast = isLive && de > 0 ? (expenses / de) * dip : expenses;
    const pctTime = dip > 0 ? Math.min(100, (de / dip) * 100) : 0;
    const hasBudget = budget > 0;
    const pctBudgetRaw = hasBudget ? (expenses / budget) * 100 : 0;
    const pctBudget = Math.min(120, pctBudgetRaw); // clamp for ring rendering only
    const paceDelta = hasBudget && isLive ? pctTime - pctBudgetRaw : null;
    const verdict = classifyPace(pctBudgetRaw, pctTime, hasBudget);
    return { expenses, budget, forecast, dip, de, isLive, hasBudget, pctTime, pctBudget, pctBudgetRaw, paceDelta, verdict };
  }, [transactions, categories, filterTransactionsByMonth, filterMode,
      effectiveStartDate, effectiveEndDate, selectedMonth, selectedYear, isCurrentMonth]);

  const s = STATUS[data.verdict];
  const m = n => fmtCompact(n, sym);
  const muted   = isDark ? 'rgba(248,250,252,0.45)' : '#94a3b8';
  const textSub = isDark ? 'rgba(248,250,252,0.68)' : '#475569';
  const textMain = isDark ? '#f8fafc' : '#0f172a';
  const trackDim = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const timeDim  = isDark ? 'rgba(255,255,255,0.22)' : '#d1d5db';
  const cardBg   = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const divider = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  let verdictText;
  if (!data.hasBudget) {
    verdictText = 'Set a monthly budget to track your pace.';
  } else if (!data.isLive) {
    const d = data.expenses - data.budget;
    verdictText = d > 0 ? `Overshot by ${m(d)} this period.` : `Stayed ${m(Math.abs(d))} under budget.`;
  } else {
    const d = data.forecast - data.budget;
    if (data.verdict === 'green') verdictText = `On track · forecasting ${m(Math.abs(d))} under budget.`;
    else if (data.verdict === 'amber') verdictText = `Tight · forecast ~${m(Math.abs(d))} ${d > 0 ? 'over' : 'under'}.`;
    else verdictText = `Off pace · cut ~${m(d)} to recover.`;
  }

  const CX = 44, CY = 44, R_OUT = 37, R_IN = 27, SW = 6;
  const absDelta = data.paceDelta !== null ? Math.abs(data.paceDelta) : null;
  const ahead = data.paceDelta !== null && data.paceDelta >= 0;

  if (loading) {
    return (
      <Box sx={{ background: cardBg, border: cardBorder, borderRadius: '16px', p: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Skeleton width={120} height={12} />
        <Box sx={{ display: 'flex', gap: '18px' }}>
          <Skeleton variant="circular" width={88} height={88} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
            <Skeleton width={56} height={30} />
            <Skeleton width={110} height={12} />
            <Skeleton width={100} height={12} />
          </Box>
        </Box>
        <Skeleton width="80%" height={13} />
      </Box>
    );
  }

  return (
    <Box sx={{
      background: cardBg,
      backdropFilter: 'blur(20px)',
      border: cardBorder,
      borderRadius: '16px',
      p: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.25)' : '0 2px 12px rgba(0,0,0,0.05)',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: muted, flex: 1 }}>
          {t('raceTrack.title', 'Month Pace')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: s.fg, boxShadow: `0 0 6px ${s.fg}` }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: s.fg }}>
            {t(`raceTrack.status.${data.verdict}`, s.label)}
          </Typography>
        </Box>
      </Box>

      {/* ── Rings + Right panel ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>

        {/* Activity rings SVG — rotated so arcs start at 12 o'clock */}
        <Box sx={{ position: 'relative', flexShrink: 0, width: 88, height: 88 }}>
          <svg width="88" height="88" viewBox="0 0 88 88" style={{ display: 'block' }}>
            <g transform={`rotate(-90 ${CX} ${CY})`}>
              {/* Outer track */}
              <circle cx={CX} cy={CY} r={R_OUT} fill="none" stroke={trackDim} strokeWidth={SW} />
              {/* Outer arc: time elapsed */}
              <circle cx={CX} cy={CY} r={R_OUT} fill="none" stroke={timeDim} strokeWidth={SW}
                strokeLinecap="round" {...arcProps(R_OUT, data.pctTime)}
                style={{ transition: 'stroke-dasharray 0.7s cubic-bezier(0.4,0,0.2,1)' }} />
              {/* Inner track */}
              <circle cx={CX} cy={CY} r={R_IN} fill="none" stroke={trackDim} strokeWidth={SW} />
              {/* Inner arc: budget spent */}
              <circle cx={CX} cy={CY} r={R_IN} fill="none" stroke={s.fg} strokeWidth={SW}
                strokeLinecap="round" {...arcProps(R_IN, data.pctBudget)}
                style={{ transition: 'stroke-dasharray 0.7s cubic-bezier(0.4,0,0.2,1)', filter: `drop-shadow(0 0 4px ${s.fg}88)` }} />
            </g>
          </svg>
          {/* Day counter centred inside rings */}
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: textMain, fontVariantNumeric: 'tabular-nums' }}>
              {data.de}
            </Typography>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: muted, lineHeight: 1.3 }}>
              /{data.dip}d
            </Typography>
          </Box>
        </Box>

        {/* Right: pace delta + legend */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {absDelta !== null ? (
            <>
              <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', color: s.fg, fontVariantNumeric: 'tabular-nums' }}>
                {absDelta.toFixed(0)}%
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: s.fg, lineHeight: 1 }}>
                {ahead ? '↓ under pace' : '↑ over pace'}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: textMain }}>
              {m(data.expenses)}
            </Typography>
          )}

          {/* Ring legend: what each ring means */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '6px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{ width: 10, height: 3, borderRadius: '2px', background: timeDim, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 10.5, color: muted, lineHeight: 1 }}>
                {data.pctTime.toFixed(0)}% of month elapsed
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{ width: 10, height: 3, borderRadius: '2px', background: s.fg, flexShrink: 0, boxShadow: `0 0 4px ${s.fg}` }} />
              <Typography sx={{ fontSize: 10.5, color: muted, lineHeight: 1 }}>
                {data.pctBudgetRaw.toFixed(0)}% of budget spent
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Verdict ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '7px', pt: '10px', borderTop: `1px solid ${divider}` }}>
        {data.verdict === 'green'
          ? <CheckCircle size={13} color={s.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          : data.verdict === 'unknown'
          ? <Target size={13} color={s.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          : <AlertTriangle size={13} color={s.fg} style={{ flexShrink: 0, marginTop: 2 }} />}
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.4, color: textSub }}>
          {verdictText}
        </Typography>
      </Box>
    </Box>
  );
}
